/**
 * CoachPlaybook - Help Bubble Component
 * Version: 0.5.146
 * Description: Contextual help bubbles to guide users through the interface
 * 
 * v0.4.5: Changed tip icon from pointing hand to speech bubble
 */

import React, { useState, useEffect } from 'react';

const HelpBubble = ({
  message,
  isVisible = true,
  position = 'bottom',  // 'top' | 'bottom' | 'left' | 'right'
  type = 'info',  // 'info' | 'success' | 'warning' | 'tip'
  autoHide = false,
  autoHideDelay = 5000,
  onDismiss,
  targetRef,
  animate = true,
  showPointer = true
}) => {
  const [visible, setVisible] = useState(isVisible);
  
  useEffect(() => {
    setVisible(isVisible);
  }, [isVisible]);
  
  useEffect(() => {
    if (autoHide && visible) {
      const timer = setTimeout(() => {
        setVisible(false);
        onDismiss?.();
      }, autoHideDelay);
      
      return () => clearTimeout(timer);
    }
  }, [autoHide, autoHideDelay, visible, onDismiss]);
  
  if (!visible) return null;
  
  const typeStyles = {
    info: {
      backgroundColor: '#3b82f6',
      borderColor: '#2563eb',
      icon: '💡'
    },
    success: {
      backgroundColor: '#10b981',
      borderColor: '#059669',
      icon: '✅'
    },
    warning: {
      backgroundColor: '#f59e0b',
      borderColor: '#d97706',
      icon: '⚠️'
    },
    tip: {
      backgroundColor: '#8b5cf6',
      borderColor: '#7c3aed',
      icon: '👆'
    }
  };
  
  const currentType = typeStyles[type] || typeStyles.info;
  
  const positionStyles = {
    top: {
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginBottom: '8px'
    },
    bottom: {
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginTop: '8px'
    },
    left: {
      right: '100%',
      top: '50%',
      transform: 'translateY(-50%)',
      marginRight: '8px'
    },
    right: {
      left: '100%',
      top: '50%',
      transform: 'translateY(-50%)',
      marginLeft: '8px'
    }
  };
  
  const pointerStyles = {
    top: {
      bottom: '-6px',
      left: '50%',
      transform: 'translateX(-50%) rotate(45deg)'
    },
    bottom: {
      top: '-6px',
      left: '50%',
      transform: 'translateX(-50%) rotate(45deg)'
    },
    left: {
      right: '-6px',
      top: '50%',
      transform: 'translateY(-50%) rotate(45deg)'
    },
    right: {
      left: '-6px',
      top: '50%',
      transform: 'translateY(-50%) rotate(45deg)'
    }
  };
  
  return (
    <div
      style={{
        ...styles.container,
        ...positionStyles[position],
        backgroundColor: currentType.backgroundColor,
        borderColor: currentType.borderColor,
        animation: animate ? 'helpBubbleIn 0.3s ease-out' : 'none'
      }}
    >
      {/* Icon */}
      <span style={styles.icon}>{currentType.icon}</span>
      
      {/* Message */}
      <span style={styles.message}>{message}</span>
      
      {/* Dismiss Button */}
      {onDismiss && (
        <button
          style={styles.dismissButton}
          onClick={(e) => {
            e.stopPropagation();
            setVisible(false);
            onDismiss();
          }}
        >
          ✕
        </button>
      )}
      
      {/* Pointer Arrow */}
      {showPointer && (
        <div
          style={{
            ...styles.pointer,
            ...pointerStyles[position],
            backgroundColor: currentType.backgroundColor
          }}
        />
      )}
      
      {/* CSS Keyframes */}
      <style>
        {`
          @keyframes helpBubbleIn {
            0% {
              opacity: 0;
              transform: translateX(-50%) translateY(10px);
            }
            100% {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
          
          @keyframes helpBubbleBounce {
            0%, 100% { transform: translateX(-50%) translateY(0); }
            50% { transform: translateX(-50%) translateY(-5px); }
          }
        `}
      </style>
    </div>
  );
};

// Floating help bubble (not attached to element)
export const FloatingHelpBubble = ({
  message,
  isVisible,
  position = { top: '20px', left: '50%' },
  type = 'info',
  autoHide = true,
  autoHideDelay = 4000,
  onDismiss
}) => {
  const [visible, setVisible] = useState(isVisible);
  
  useEffect(() => {
    setVisible(isVisible);
  }, [isVisible]);
  
  useEffect(() => {
    if (autoHide && visible) {
      const timer = setTimeout(() => {
        setVisible(false);
        onDismiss?.();
      }, autoHideDelay);
      
      return () => clearTimeout(timer);
    }
  }, [autoHide, autoHideDelay, visible, onDismiss]);
  
  if (!visible) return null;
  
  const typeStyles = {
    info: { backgroundColor: '#3b82f6', icon: '💡' },
    success: { backgroundColor: '#10b981', icon: '✅' },
    warning: { backgroundColor: '#f59e0b', icon: '⚠️' },
    tip: { backgroundColor: '#6366f1', icon: '💬' }
  };
  
  const currentType = typeStyles[type] || typeStyles.info;
  
  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 1000,
        ...position,
        transform: position.left === '50%' ? 'translateX(-50%)' : 'none'
      }}
    >
      <div
        style={{
          ...styles.floatingContainer,
          backgroundColor: currentType.backgroundColor,
          animation: 'helpBubbleBounce 1s ease-in-out infinite'
        }}
      >
        <span style={styles.icon}>{currentType.icon}</span>
        <span style={styles.message}>{message}</span>
        {onDismiss && (
          <button
            style={styles.dismissButton}
            onClick={() => {
              setVisible(false);
              onDismiss();
            }}
          >
            ✕
          </button>
        )}
      </div>
      
      <style>
        {`
          @keyframes helpBubbleBounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }
        `}
      </style>
    </div>
  );
};

// Help Messages Context
export const HELP_MESSAGES = {
  SELECT_POSITION: '👈 Select a position to assign a player',
  SELECT_PLAYER: '👤 Choose a player from your roster',
  PLACE_PLAYER: (name) => `👆 Tap court to place ${name}`,
  PLAYER_PLACED: (name) => `✅ ${name} placed! Draw movement or assign next`,
  DRAW_PATH: '✏️ Drag from player to draw movement path',
  MARK_SHOOTER: '⭐ Long press player to mark as shooter',
  SAVE_PLAY: "💾 Don't forget to save your play!",
  ADD_ZONE: '🎨 Click court to add coaching zone',
  SWITCH_MODE: '🔄 Switch to Player mode to assign actual players'
};

const styles = {
  container: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '2px solid',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 100,
    maxWidth: '280px'
  },
  floatingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    borderRadius: '10px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
  },
  icon: {
    fontSize: '18px',
    flexShrink: 0
  },
  message: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '1.4'
  },
  dismissButton: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: '#fff',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginLeft: '4px'
  },
  pointer: {
    position: 'absolute',
    width: '12px',
    height: '12px'
  }
};

export default HelpBubble;
